import { Trans, useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import EcoV2 from '../../../assets/img/EcoV2.png';
import { colors } from '~/styles/colors';
import { getContainerModal } from '~/helpers/modalImage';
import { Image } from 'antd';
import { useStateVar } from '~/helpers/useStateVar';
import { Input } from '~/components/Input';
import { Checkbox } from '~/components/Checkbox';
import {
  ContentInnerCollapsible, ContentOuterCollapsible, CustomSelect, HeaderCollapsible, IconCollapsible, TitleCollapsible,
} from '../styles';
import { useEffect, useRef, useState } from 'react';
import { comportamentoInicialProgHorarioOptions, ltiModeEcoOptions } from '../constants';
import { Select } from '~/components/Select';
import { InputCalculator } from '~/components/InputCalculator';
import { BaseComponentProps, ModoEcoData } from '../types';

export type { ModoEcoData };

const DEFAULT_SETPOINT_VALUES = [16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30];
const LTC_RANGE_VALUES = Array.from({ length: 31 }, (_, i) => i);

type ModoEcoProps = {
  onChange: (data: ModoEcoData) => void;
  irCommands: BaseComponentProps['irCommands'];
  isMultipleDevProg?: boolean;
  initialData?: Partial<ModoEcoData>;
};

export const ModoEco = ({
  onChange,
  irCommands,
  isMultipleDevProg,
  initialData,
}: ModoEcoProps): JSX.Element => {
  const { t } = useTranslation();
  const [state, render, setState] = useStateVar({
    setpoint: initialData?.setpoint ?? (irCommands[0]?.TEMPER?.toString() ?? ''),
    ltc_degree: initialData?.ltc_degree ?? (irCommands[0]?.TEMPER?.toString() ?? ''),
    lti_cmd: initialData?.lti_cmd ?? 'NO_ACTION',
    lti_degree: initialData?.lti_degree ?? ((initialData?.lti_cmd === 'FAN' || initialData?.lti_cmd === 'OFF') ? (irCommands[0]?.TEMPER?.toString() ?? '') : ''),
    hysteresisChecked: initialData ? !!(initialData?.upperHysteresis || initialData?.lowerHysteresis) : true,
    upperHysteresis: initialData?.upperHysteresis ?? '1.0',
    lowerHysteresis: initialData?.lowerHysteresis ?? '1.0',
    upperHysteresisError: null as string | null,
    lowerHysteresisError: null as string | null,
    initialBehaviorProgrammingHour: initialData?.initialBehaviorProgrammingHour ?? '',
  });

  useEffect(() => {
    onChange({
      setpoint: state.setpoint,
      ltc_degree: state.ltc_degree,
      lti_degree: state.lti_degree,
      lti_cmd: state.lti_cmd,
      upperHysteresis: state.upperHysteresis,
      lowerHysteresis: state.lowerHysteresis,
      initialBehaviorProgrammingHour: state.initialBehaviorProgrammingHour,
    });
  }, [state.setpoint, state.ltc_degree, state.lti_degree, state.lti_cmd, state.upperHysteresis, state.lowerHysteresis, state.initialBehaviorProgrammingHour]);

  const [open, setOpen] = useState(false);
  const [height, setHeight] = useState(0);
  const innerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (innerRef.current) {
      setHeight(open ? innerRef.current.scrollHeight : 0);
    }
  }, [open]);

  const ltcModeOptions = [{ label: t('refrigerar'), value: 'COOL' }];

  const baseValues = isMultipleDevProg || irCommands.length === 0
    ? DEFAULT_SETPOINT_VALUES
    : irCommands.map((command) => command.TEMPER);
  const setpointNum = Number(state.setpoint);
  const ltcPossibleValues = LTC_RANGE_VALUES.filter((v) => v >= setpointNum);
  const ltiPossibleValues = baseValues.filter((v) => v <= setpointNum);

  function increaseDecrease(value: string, type: 'ltc' | 'lti') {
    setState({ [`${type}_degree`]: value });
  }

  return (
    <Flex>
      <Flex flexDirection="column" style={{ gap: '16px' }}>
        <div style={{ fontSize: '12px', textAlign: 'justify' }}>
          <Trans i18nKey="detalhesModoControle6" components={[<hr style={{ borderColor: colors.Grey200, opacity: 0.3 }} />]} />
        </div>
        <Image
          width={376}
          height={209}
          src={EcoV2}
          preview={{
            getContainer: getContainerModal('schedule-modal'),
          }}
        />
        <InputCalculator
          data-testid="modo-eco-setpoint-input"
          allowManualInput
          label="Setpoint [°C]"
          value={state.setpoint ? state.setpoint.toString() : ''}
          style={{ height: '50px', width: '400px' }}
          possibleValues={baseValues}
          unity="ºC"
          onIncreaseDecrease={(value: string) => {
            const newSetpoint = Number(value);
            setState({
              setpoint: value,
              ...(Number(state.ltc_degree) < newSetpoint && { ltc_degree: value }),
              ...((state.lti_cmd === 'FAN' || state.lti_cmd === 'OFF') && Number(state.lti_degree) > newSetpoint && { lti_degree: value }),
            });
          }}
          isSetpoint
          disabled={!isMultipleDevProg && irCommands.length === 0}
        />
        <Flex
          style={{
            justifyContent: 'space-between',
            width: '400px',
          }}
        >
          <Select
            data-testid="modo-eco-ltc-cmd-select"
            options={ltcModeOptions}
            value={ltcModeOptions[0]}
            placeholder={t('acaoLTC')}
            onSelect={() => {}}
            disabled
            style={{ border: '0.7px solid #818181', width: '188px', marginBottom: '16px' }}
            propLabel="label"
            notNull
          />
          <InputCalculator
            data-testid="modo-eco-ltc-degree-input"
            allowManualInput
            label={`${t('LTC')} [ºC]`}
            value={state.ltc_degree ? state.ltc_degree.toString() : ''}
            style={{ height: '50px' }}
            possibleValues={ltcPossibleValues}
            unity="ºC"
            onIncreaseDecrease={(value: string) => increaseDecrease(value, 'ltc')}
            isSetpoint
            lowerLimitMessage={t('limiteLTCSetpoint', { setpoint: state.setpoint })}
          />
        </Flex>
        <Flex
          style={{
            justifyContent: 'space-between',
            width: '400px',
          }}
        >
          <Select
            data-testid="modo-eco-lti-cmd-select"
            options={ltiModeEcoOptions}
            value={ltiModeEcoOptions.find((opt) => opt.value === state.lti_cmd) ?? null}
            placeholder={t('acaoLTI')}
            onSelect={(item) => {
              setState({
                lti_cmd: item.value,
                ...((item.value === 'FAN' || item.value === 'OFF') && !state.lti_degree && { lti_degree: irCommands[0]?.TEMPER?.toString() ?? '' }),
              });
            }}
            style={{ border: '0.7px solid #818181', width: (state.lti_cmd === 'FAN' || state.lti_cmd === 'OFF') ? '188px' : '400px', marginBottom: '16px' }}
            propLabel="label"
            notNull
          />
          {(state.lti_cmd === 'FAN' || state.lti_cmd === 'OFF') && (
          <InputCalculator
            data-testid="modo-eco-lti-degree-input"
            allowManualInput
            label={`${t('LTI')} [ºC]`}
            value={state.lti_degree ? state.lti_degree.toString() : ''}
            style={{ height: '50px' }}
            possibleValues={ltiPossibleValues}
            unity="ºC"
            onIncreaseDecrease={(value: string) => increaseDecrease(value, 'lti')}
            isSetpoint
            disabled={!isMultipleDevProg && irCommands.length === 0}
            upperLimitMessage={t('limiteLTISetpoint', { setpoint: state.setpoint })}
          />
          )}
        </Flex>
        <HeaderCollapsible
          data-testid="modo-eco-advanced-toggle"
          onClick={() => setOpen((s) => !s)}
          aria-expanded={open}
          aria-controls="collapsible-advanced-content"
          style={{ padding: '0px' }}
        >
          <TitleCollapsible>{t('configuracoesAdicionais')}</TitleCollapsible>
          <IconCollapsible $open={open} aria-hidden>
            ▼
          </IconCollapsible>
        </HeaderCollapsible>
        <ContentOuterCollapsible id="collapsible-advanced-content" $height={height} $open={open} style={{ padding: '0px', gap: '16px' }}>
          <ContentInnerCollapsible
            ref={innerRef}
            style={{
              padding: '0px', gap: '16px', display: 'flex', flexDirection: 'column',
            }}
          >
            <CustomSelect
              data-testid="modo-eco-initial-behavior-select"
              options={comportamentoInicialProgHorarioOptions}
              value={comportamentoInicialProgHorarioOptions.find((opt) => opt.value === state.initialBehaviorProgrammingHour) ?? null}
              placeholder={t('comportamentoInicialProgHorario')}
              onSelect={(item) => setState({ initialBehaviorProgrammingHour: item.value })}
              style={{ width: '400px', marginBottom: '0px' }}
              notNull
            />
            <Flex alignItems="center">
              <label
                onClick={() => {
                  const next = !state.hysteresisChecked;
                  setState({
                    hysteresisChecked: next,
                    ...(!next && {
                      upperHysteresis: '',
                      lowerHysteresis: '',
                      initialBehaviorProgrammingHour: '',
                    }),
                  });
                  render();
                }}
              >
                <Checkbox data-testid="modo-eco-hysteresis-checkbox" checked={state.hysteresisChecked} />
              </label>
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 'bold',
                  marginTop: '2px',
                  marginLeft: '8px',
                }}
              >
                {`${t('habilitarHistereses')}`}
              </div>
            </Flex>
            <Flex
              flexWrap="nowrap"
              flexDirection="row"
              style={{
                width: '400px',
                justifyContent: 'space-between',
              }}
            >
              <Input
                data-testid="modo-eco-upper-hysteresis-input"
                label={t('histereseSuperiorC')}
                value={state.upperHysteresis}
                placeholder="0.0"
                suffix="°C"
                style={{ width: '188px' }}
                error={state.upperHysteresisError}
                onChange={(e) => {
                  const raw = e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '');
                  const dotIndex = raw.indexOf('.');
                  const normalized = dotIndex === -1 ? raw : raw.slice(0, dotIndex + 1) + raw.slice(dotIndex + 1).replace(/\./g, '');
                  const parts = normalized.split('.');
                  const value = parts.length > 1 && parts[1].length > 1 ? parseFloat(normalized).toFixed(1) : normalized;
                  const num = parseFloat(value);
                  const invalid = value !== '' && (Number.isNaN(num) || num < 0 || num > 5);
                  setState({ upperHysteresis: value, upperHysteresisError: invalid ? t('valorEntre', { min: '0.0', max: '5.0' }) : null });
                }}
                disabled={!state.hysteresisChecked}
              />
              <Input
                data-testid="modo-eco-lower-hysteresis-input"
                label={t('histereseInferiorC')}
                value={state.lowerHysteresis}
                placeholder="0.0"
                suffix="°C"
                style={{ width: '188px' }}
                error={state.lowerHysteresisError}
                onChange={(e) => {
                  const raw = e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '');
                  const dotIndex = raw.indexOf('.');
                  const normalized = dotIndex === -1 ? raw : raw.slice(0, dotIndex + 1) + raw.slice(dotIndex + 1).replace(/\./g, '');
                  const parts = normalized.split('.');
                  const value = parts.length > 1 && parts[1].length > 1 ? parseFloat(normalized).toFixed(1) : normalized;
                  const num = parseFloat(value);
                  const invalid = value !== '' && (Number.isNaN(num) || num < 0 || num > 5);
                  setState({ lowerHysteresis: value, lowerHysteresisError: invalid ? t('valorEntre', { min: '0.0', max: '5.0' }) : null });
                }}
                disabled={!state.hysteresisChecked}
              />
            </Flex>
          </ContentInnerCollapsible>
        </ContentOuterCollapsible>
      </Flex>
    </Flex>
  );
};
